import sidebarData from "../../../sidebar.json";

const useNavBarProvider = () => {
  /**
   * Filter sidebar items based on user permissions
   * @param {Array} permissions - Array of permission objects from user's role, each with a 'key' property
   * @param {string} role - Current user's role name (e.g., "Súper Administrador"). Used to hide
   *   items flagged with `requireSuperAdmin` (e.g. Credenciales/Settings) from non-super-admins.
   * @returns {Promise} - Promise resolving to filtered sidebar data
   */
  const getNavBarItemsProvider = (permissions = [], role = null) => {
    const allItems = sidebarData.data;
    const isSuperAdmin = role === "Súper Administrador";

    // Create a Set of permission keys for faster lookup
    const permissionKeys = new Set(permissions.map(p => p.key));

    /**
     * Recursively filter sidebar items based on permissions
     * For items with children (optionSuboption), show parent if ANY child has permission
     * @param {Object} item - Sidebar item to check
     * @returns {Object|null} - Filtered item or null if no permission
     */
    const filterItem = (item) => {
      // If item has children, recursively filter them
      if (item.optionSuboption && item.optionSuboption.length > 0) {
        const filteredChildren = item.optionSuboption
          .map(child => filterItem(child))
          .filter(child => child !== null);

        // Show parent if any children are visible
        if (filteredChildren.length > 0) {
          return {
            ...item,
            optionSuboption: filteredChildren,
          };
        }
        return null;
      }

      // Super-admin only item: hide from anyone who isn't a super-admin.
      if (item.requireSuperAdmin && !isSuperAdmin) {
        return null;
      }

      // Leaf item: check if user has permission
      if (item.permissionKey) {
        return permissionKeys.has(item.permissionKey) ? item : null;
      }

      // If no permissionKey defined, don't show (for safety)
      return null;
    };

    // Filter all top-level items
    const filteredItems = allItems
      .map(item => filterItem(item))
      .filter(item => item !== null);

    return Promise.resolve({
      data: {
        status: "success",
        data: filteredItems
      }
    });
  };

  return {
    getNavBarItemsProvider
  };
};

export default useNavBarProvider;
